// src/features/system/orchestrator/components/ScopeCenterPanel.tsx
// ────────────────────────────────────────────────────────────────────────────
// Scope Management Center — consolidates all scope tools under one roof.
//
// Sub-tabs:
//   • Migration  — export/import scope trees (ScopeMigrationPanel)
//   • Cleaner    — preview & purge scope data (ScopeCleanerPanel)
//   • KPI        — namespace-driven analytics (ScopeKpiPanel)
// ────────────────────────────────────────────────────────────────────────────
import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import ScopeMigrationPanel from './scopeMigration';
import ScopeCleanerPanel from './ScopeCleanerPanel';
import ScopeKpiPanel from './ScopeKpiPanel';

const SUB_TABS = ['Migration', 'Cleaner', 'KPI Analytics'] as const;
type ScopeSub = typeof SUB_TABS[number];

export default function ScopeCenterPanel() {
  const [sp] = useSearchParams();
  const [sub, setSub] = useState<ScopeSub>(() => {
    const v = sp.get('scopeTab');
    return SUB_TABS.includes(v as ScopeSub) ? (v as ScopeSub) : 'Migration';
  });

  // Sync with URL param (BubbleNav deep-linking)
  useEffect(() => {
    const v = sp.get('scopeTab');
    if (v && SUB_TABS.includes(v as ScopeSub) && v !== sub) setSub(v as ScopeSub);
  }, [sp]);

  return (
    <div className="h-full flex flex-col gap-3 min-h-0 overflow-hidden">
      {/* ── Sub-tab pills ──────────────────────────────────────── */}
      <div className="flex gap-2 shrink-0">
        {SUB_TABS.map(tab => (
          <button
            key={tab}
            onClick={() => setSub(tab)}
            className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
              sub === tab
                ? 'bg-blue-600 text-white'
                : 'pac-glass-btn text-neutral-400 hover:border-neutral-500 hover:text-neutral-200'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* ── Active panel ───────────────────────────────────────── */}
      <div className="flex-1 min-h-0 overflow-hidden">
        {sub === 'Migration'      && <ScopeMigrationPanel />}
        {sub === 'Cleaner'        && <ScopeCleanerPanel />}
        {sub === 'KPI Analytics'  && <ScopeKpiPanel />}
      </div>
    </div>
  );
}
